import {
  AmbientLight,
  DirectionalLight,
  EquirectangularReflectionMapping,
  NeutralToneMapping,
  PCFSoftShadowMap,
  PerspectiveCamera,
  PMREMGenerator,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
  type Texture,
} from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';

export interface PlayBounds {
  width: number;
  depth: number;
  centerX?: number;
  centerZ?: number;
  /**
   * Override the landscape-aspect baseline elevation (degrees). The default
   * is 50° at landscape, lerping toward 88° (near top-down) as the viewport
   * goes portrait. A taller layout like Pyramid wants a steeper baseline so
   * the back of the table doesn't disappear over the horizon.
   */
  baselineElevationDeg?: number;
}

export interface ThreeScene {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  controls: OrbitControls;
  loadHDRI(url: string): Promise<void>;
  resize(): void;
  setBounds(bounds: PlayBounds): void;
  dispose(): void;
}

export function createScene(canvas: HTMLCanvasElement): ThreeScene {
  const renderer = new WebGLRenderer({ canvas, antialias: true, alpha: false });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;
  renderer.shadowMap.needsUpdate = true;

  const scene = new Scene();

  const camera = new PerspectiveCamera(35, 1, 0.05, 50);
  camera.position.set(0, 9, 7.5);
  camera.lookAt(0, 0, 0);

  const ambient = new AmbientLight(0xffffff, 0.35);
  scene.add(ambient);

  let hdriLoaded = false;

  const key = new DirectionalLight(0xfff0e0, 1.5);
  key.position.set(3, 9, 5);
  key.castShadow = true;
  key.shadow.mapSize.set(2048, 2048);
  key.shadow.camera.left = -6;
  key.shadow.camera.right = 6;
  key.shadow.camera.top = 5;
  key.shadow.camera.bottom = -5;
  key.shadow.camera.near = 0.5;
  key.shadow.camera.far = 25;
  key.shadow.bias = -0.0003;
  key.shadow.radius = 2;
  scene.add(key);

  const controls = new OrbitControls(camera, canvas);
  controls.enablePan = false;
  controls.enableRotate = false;
  controls.enableZoom = false;
  controls.target.set(0, 0, 0);
  controls.update();

  const pmrem = new PMREMGenerator(renderer);
  pmrem.compileEquirectangularShader();

  async function loadHDRI(url: string) {
    const texture: Texture = await new Promise((resolve, reject) => {
      new RGBELoader().load(url, resolve, undefined, reject);
    });
    texture.mapping = EquirectangularReflectionMapping;
    const env = pmrem.fromEquirectangular(texture).texture;
    scene.environment = env;
    scene.environmentIntensity = 0.45;
    if (!hdriLoaded) {
      ambient.intensity = 0.05;
      key.intensity = 2.2;
      hdriLoaded = true;
    }
    texture.dispose();
  }

  let playBounds: PlayBounds = { width: 9, depth: 6, centerX: 0, centerZ: 0 };

  function setBounds(b: PlayBounds) {
    playBounds = { centerX: 0, centerZ: 0, ...b };
    resize();
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio, 1.5);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    const aspect = w / h;
    camera.aspect = aspect;

    // Lerp elevation + FOV based on aspect; then pick the distance that makes
    // the game's known play bounds fit width and depth simultaneously.
    const refAspect = 1.6;
    const portraitAspect = 0.55;
    const t = Math.max(0, Math.min(1, (refAspect - aspect) / (refAspect - portraitAspect)));
    const baseElev = playBounds.baselineElevationDeg ?? 50;
    const elevationDeg = baseElev + t * (88 - baseElev);
    const fovDeg = 35 + t * 12;          // 35° → 47°
    const elev = (elevationDeg * Math.PI) / 180;
    const vFov = (fovDeg * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);

    const PAD = 1.25;
    const distForWidth = (playBounds.width * PAD) / 2 / Math.tan(hFov / 2);
    // Depth foreshortens as elevation drops below 90°; sin(elev) accounts for it.
    const distForDepth =
      ((playBounds.depth * PAD) / 2) * Math.max(0.4, Math.sin(elev)) /
      Math.tan(vFov / 2);
    const dist = Math.max(distForWidth, distForDepth);

    const cx = playBounds.centerX ?? 0;
    const cz = playBounds.centerZ ?? 0;

    camera.fov = fovDeg;
    camera.position.set(
      cx,
      dist * Math.sin(elev),
      cz + dist * Math.cos(elev),
    );
    camera.lookAt(cx, 0, cz);
    camera.updateProjectionMatrix();
    controls.target.set(cx, 0, cz);
    controls.update();
  }

  function dispose() {
    controls.dispose();
    pmrem.dispose();
    renderer.dispose();
  }

  resize();
  window.addEventListener('resize', resize);

  return { scene, camera, renderer, controls, loadHDRI, resize, setBounds, dispose };
}
